import { ThemeEvents, VariantUpdateEvent } from '@theme/events';
import { Component } from '@theme/component';

const SUBSCRIPTION_PRICE_MULTIPLIER = 0.9;
const SELLING_PLAN_INPUT_SELECTOR = 'form.shopify-product-form input[name="selling_plan"][data-sls-selling_plan]';
const DISCOUNTABLE_PRICE_SELECTOR = '.price__regular .price, .price__sale .price-item--sale.price';
const NUMBER_PATTERN = /-?\d[\d.,]*(?:[.,]\d+)?/g;

/**
 * @typedef {Object} ProductPriceRefs
 * @property {HTMLElement} priceContainer
 * @property {HTMLElement} [volumePricingNote]
 */

/**
 * A custom element that displays a product price.
 * This component listens for variant update events and updates the price display accordingly.
 * It handles price updates from two different sources:
 * 1. Variant picker (in quick add modal or product page)
 * 2. Swatches variant picker (in product cards)
 *
 * @extends {Component<ProductPriceRefs>}
 */
class ProductPrice extends Component {
  /** @type {HTMLInputElement | null} */
  #sellingPlanInput = null;

  /** @type {AbortController} */
  #sellingPlanInputAbortController = new AbortController();

  #sellingPlanObserver = new MutationObserver(() => {
    this.#bindSellingPlanInput();
    this.#observeSellingPlanScope();
    this.#syncSubscriptionPrice();
  });

  connectedCallback() {
    super.connectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.addEventListener(ThemeEvents.variantUpdate, this.updatePrice);
    this.#bindSellingPlanInput();
    this.#observeSellingPlanScope();
    this.#syncSubscriptionPrice();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    closestSection?.removeEventListener(ThemeEvents.variantUpdate, this.updatePrice);
    this.#sellingPlanObserver.disconnect();
    this.#sellingPlanInputAbortController.abort();
  }

  /**
   * Updates the price and volume pricing note.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updatePrice = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    const { priceContainer, volumePricingNote } = this.refs;
    // Find the new product-price element in the updated HTML
    const newProductPrice = event.detail.data.html.querySelector(
      `product-price[data-block-id="${this.dataset.blockId}"]`
    );
    if (!newProductPrice) return;

    // Update price container
    const newPrice = newProductPrice.querySelector('[ref="priceContainer"]');
    if (newPrice && priceContainer) {
      priceContainer.replaceWith(newPrice);
    }
    this.#bindSellingPlanInput();
    this.#observeSellingPlanScope();
    this.#syncSubscriptionPrice();

    // Update volume pricing note
    const newNote = newProductPrice.querySelector('[ref="volumePricingNote"]');

    if (!newNote) {
      volumePricingNote?.remove();
    } else if (!volumePricingNote) {
      // Use newPrice since priceContainer was just replaced and now points to the detached element
      newPrice?.insertAdjacentElement('afterend', /** @type {Element} */(newNote.cloneNode(true)));
    } else {
      volumePricingNote.replaceWith(newNote);
    }

    // Update installments (SPI banner) variant ID to trigger payment terms re-render
    const input_selector = `#product-form-installment-${this.dataset.blockId} input[name="id"]`;
    const installmentsInput = /** @type {HTMLInputElement|null} */ (this.querySelector(input_selector));
    if (installmentsInput) {
      installmentsInput.value = event.detail.resource?.id ?? '';
      installmentsInput.dispatchEvent(new Event('change', { bubbles: true }));
    }
  };

  #bindSellingPlanInput() {
    const sellingPlanInput = this.#getSellingPlanInput();
    if (sellingPlanInput === this.#sellingPlanInput) return;

    this.#sellingPlanInputAbortController.abort();
    this.#sellingPlanInputAbortController = new AbortController();
    this.#sellingPlanInput = sellingPlanInput;

    if (!sellingPlanInput) return;

    const { signal } = this.#sellingPlanInputAbortController;
    sellingPlanInput.addEventListener('change', this.#syncSubscriptionPrice, { signal });
    sellingPlanInput.addEventListener('input', this.#syncSubscriptionPrice, { signal });
  }

  #observeSellingPlanScope() {
    this.#sellingPlanObserver.disconnect();

    const sellingPlanForm = this.#sellingPlanInput?.closest('form.shopify-product-form');
    if (sellingPlanForm) {
      this.#sellingPlanObserver.observe(sellingPlanForm, {
        attributes: true,
        attributeFilter: ['value'],
        childList: true,
        subtree: true,
      });
      return;
    }

    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;

    this.#sellingPlanObserver.observe(closestSection, {
      childList: true,
      subtree: true,
    });
  }

  /**
   * @returns {HTMLInputElement | null}
   */
  #getSellingPlanInput() {
    const closestSection = this.closest('.shopify-section, dialog');
    const sellingPlanInputs = /** @type {HTMLInputElement[]} */ (
      Array.from(closestSection?.querySelectorAll(SELLING_PLAN_INPUT_SELECTOR) ?? [])
    );

    if (sellingPlanInputs.length === 0) return null;

    return (
      sellingPlanInputs.find((input) => {
        const productForm = input.closest('product-form-component');
        return productForm instanceof HTMLElement && productForm.dataset.productId === this.dataset.productId;
      }) ??
      sellingPlanInputs[0] ??
      null
    );
  }

  #syncSubscriptionPrice = () => {
    const priceContainer = this.querySelector('[ref="priceContainer"]');
    if (!(priceContainer instanceof HTMLElement)) return;

    const hasSubscription = Boolean(this.#sellingPlanInput?.value);

    for (const priceElement of priceContainer.querySelectorAll(DISCOUNTABLE_PRICE_SELECTOR)) {
      if (priceElement instanceof HTMLElement) {
        syncDiscountedElementText(priceElement, hasSubscription, true);
      }
    }

    const unitPrice = priceContainer.querySelector('.unit-price');
    if (unitPrice instanceof HTMLElement) {
      syncDiscountedUnitPrice(unitPrice, hasSubscription);
    }
  };
}

if (!customElements.get('product-price')) {
  customElements.define('product-price', ProductPrice);
}

/**
 * @param {HTMLElement} element
 * @param {boolean} shouldDiscount
 * @param {boolean} discountAllNumbers
 */
function syncDiscountedElementText(element, shouldDiscount, discountAllNumbers) {
  const originalText = element.dataset.subscriptionBaseText ?? element.textContent ?? '';
  element.dataset.subscriptionBaseText = originalText;

  const nextText = shouldDiscount ? getDiscountedText(originalText, discountAllNumbers) : originalText;
  if (element.textContent !== nextText) {
    element.textContent = nextText;
  }
}

/**
 * @param {HTMLElement} element
 * @param {boolean} shouldDiscount
 */
function syncDiscountedUnitPrice(element, shouldDiscount) {
  const textNode = Array.from(element.childNodes).find(
    (node) => node.nodeType === Node.TEXT_NODE && /\d/.test(node.textContent ?? '')
  );
  if (!textNode) return;

  const originalText = element.dataset.subscriptionBaseText ?? textNode.textContent ?? '';
  element.dataset.subscriptionBaseText = originalText;

  const nextText = shouldDiscount ? getDiscountedText(originalText, false) : originalText;
  if (textNode.textContent !== nextText) {
    textNode.textContent = nextText;
  }
}

/**
 * @param {string} text
 * @param {boolean} discountAllNumbers
 * @returns {string}
 */
function getDiscountedText(text, discountAllNumbers) {
  let hasDiscountedNumber = false;

  return text.replace(NUMBER_PATTERN, (numberText) => {
    if (!discountAllNumbers && hasDiscountedNumber) return numberText;

    const parsedNumber = parseLocalizedNumber(numberText);
    if (parsedNumber === null) return numberText;

    hasDiscountedNumber = true;
    return formatLocalizedNumber(parsedNumber * SUBSCRIPTION_PRICE_MULTIPLIER, numberText);
  });
}

/**
 * @param {string} text
 * @returns {number | null}
 */
function parseLocalizedNumber(text) {
  const sanitizedText = text.replace(/[^\d,.-]/g, '');
  if (!/\d/.test(sanitizedText)) return null;

  const decimalSeparator = getDecimalSeparator(sanitizedText);
  const normalizedText = decimalSeparator
    ? sanitizedText
        .replace(new RegExp(`\\${decimalSeparator === ',' ? '.' : ','}`, 'g'), '')
        .replace(decimalSeparator, '.')
    : sanitizedText.replace(/[,.]/g, '');

  const parsedNumber = Number(normalizedText);
  return Number.isFinite(parsedNumber) ? parsedNumber : null;
}

/**
 * @param {number} value
 * @param {string} originalText
 * @returns {string}
 */
function formatLocalizedNumber(value, originalText) {
  const decimalSeparator = getDecimalSeparator(originalText) ?? getDefaultDecimalSeparator();
  const fractionDigits = getFractionDigits(value, originalText, decimalSeparator);
  const groupingSeparator = getGroupingSeparator(originalText, decimalSeparator);
  const sign = value < 0 ? '-' : '';
  const [integerPart = '0', fractionPart = ''] = Math.abs(value).toFixed(fractionDigits).split('.');
  const groupedIntegerPart = groupingSeparator
    ? integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, groupingSeparator)
    : integerPart;

  return `${sign}${groupedIntegerPart}${fractionPart ? decimalSeparator + fractionPart : ''}`;
}

/**
 * @param {string} text
 * @returns {',' | '.' | null}
 */
function getDecimalSeparator(text) {
  const lastCommaIndex = text.lastIndexOf(',');
  const lastDotIndex = text.lastIndexOf('.');
  const separatorIndex = Math.max(lastCommaIndex, lastDotIndex);

  if (separatorIndex === -1) return null;

  const separator = text[separatorIndex];
  const hasBothSeparators = lastCommaIndex !== -1 && lastDotIndex !== -1;
  const fractionDigits = text.slice(separatorIndex + 1).replace(/\D/g, '').length;

  if (!hasBothSeparators && fractionDigits === 3) return null;
  return separator === ',' ? ',' : '.';
}

/**
 * @param {number} value
 * @param {string} originalText
 * @param {',' | '.'} decimalSeparator
 * @returns {number}
 */
function getFractionDigits(value, originalText, decimalSeparator) {
  const separatorIndex = originalText.lastIndexOf(decimalSeparator);
  if (separatorIndex !== -1) {
    return originalText.slice(separatorIndex + 1).replace(/\D/g, '').length;
  }

  return Number.isInteger(value) ? 0 : 2;
}

/**
 * @param {string} originalText
 * @param {',' | '.'} decimalSeparator
 * @returns {string}
 */
function getGroupingSeparator(originalText, decimalSeparator) {
  const separators = originalText.replace(/[^\d,.]/g, '').match(/[,.]/g) ?? [];
  return separators.find((separator) => separator !== decimalSeparator) ?? '';
}

/**
 * @returns {',' | '.'}
 */
function getDefaultDecimalSeparator() {
  const locale = document.documentElement.lang || navigator.language || 'es-ES';
  const decimalPart = new Intl.NumberFormat(locale).formatToParts(1.1).find((part) => part.type === 'decimal');

  return decimalPart?.value === '.' ? '.' : ',';
}
